// src/pages/onboarddeboard/employees/leave/page.js

import React, { useState } from "react";

// ─── Static Mock Data ─────────────────────────────────────────────────────────

const INITIAL_LEAVE_REQUESTS = [
  { id: 1,  employeeName: "Aarav Sharma",    department: "Engineering", leaveType: "Casual Leave",  fromDate: "2026-04-30", toDate: "2026-05-01", numberOfDays: 2, status: "Pending"  },
  { id: 2,  employeeName: "Priya Mehta",     department: "Design",      leaveType: "Sick Leave",    fromDate: "2026-04-29", toDate: "2026-04-29", numberOfDays: 1, status: "Approved" },
  { id: 3,  employeeName: "Rohit Verma",     department: "Engineering", leaveType: "Earned Leave",  fromDate: "2026-05-05", toDate: "2026-05-09", numberOfDays: 5, status: "Pending"  },
  { id: 4,  employeeName: "Sneha Patil",     department: "HR",          leaveType: "Casual Leave",  fromDate: "2026-04-28", toDate: "2026-04-28", numberOfDays: 1, status: "Rejected" },
  { id: 5,  employeeName: "Karan Joshi",     department: "Testing",     leaveType: "Work From Home",fromDate: "2026-04-29", toDate: "2026-04-29", numberOfDays: 1, status: "Approved" },
  { id: 6,  employeeName: "Divya Nair",      department: "Engineering", leaveType: "Sick Leave",    fromDate: "2026-05-02", toDate: "2026-05-03", numberOfDays: 2, status: "Pending"  },
  { id: 7,  employeeName: "Amit Kulkarni",   department: "Sales",       leaveType: "Earned Leave",  fromDate: "2026-04-27", toDate: "2026-04-30", numberOfDays: 4, status: "Approved" },
  { id: 8,  employeeName: "Pooja Desai",     department: "Finance",     leaveType: "Casual Leave",  fromDate: "2026-05-06", toDate: "2026-05-06", numberOfDays: 1, status: "Pending"  },
  { id: 9,  employeeName: "Vikas Rao",       department: "Engineering", leaveType: "Sick Leave",    fromDate: "2026-04-29", toDate: "2026-04-29", numberOfDays: 1, status: "Rejected" },
  { id: 10, employeeName: "Ananya Singh",    department: "Marketing",   leaveType: "Casual Leave",  fromDate: "2026-05-10", toDate: "2026-05-12", numberOfDays: 3, status: "Pending"  },
];

// ─── Constants ────────────────────────────────────────────────────────────────

const AVATAR_COLORS = ["#3b82f6","#8b5cf6","#10b981","#f59e0b","#ef4444","#6366f1"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getInitials = (name) => {
  if (!name) return "?";
  return name.trim().split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
};

const statusStyle = (status) => {
  const base = { borderRadius: 12, padding: "2px 12px", fontSize: 12, fontWeight: 600 };
  if (status === "Approved") return { ...base, background: "#e6f4ea", color: "#2e7d32" };
  if (status === "Pending")  return { ...base, background: "#fff8e1", color: "#f59e0b" };
  if (status === "Rejected") return { ...base, background: "#fdecea", color: "#c62828" };
  return base;
};

// ─── Main Component ───────────────────────────────────────────────────────────

const Leave = () => {
  const [leaveRequests, setLeaveRequests] = useState(INITIAL_LEAVE_REQUESTS);
  const [searchTerm,    setSearchTerm]    = useState("");
  const [statusFilter,  setStatusFilter]  = useState("All status");

  // ── Stat counts from static data ──
  const pendingCount       = leaveRequests.filter((r) => r.status === "Pending").length;
  const approvedTodayCount = leaveRequests.filter((r) => r.status === "Approved" && r.fromDate === "2026-04-29").length;
  const rejectedCount      = leaveRequests.filter((r) => r.status === "Rejected").length;
  const onLeaveTodayCount  = leaveRequests.filter((r) => r.status === "Approved" && r.fromDate <= "2026-04-29" && r.toDate >= "2026-04-29").length;

  // ── Approve / Reject (local state only) ──
  const handleApprove = (id) => {
    setLeaveRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "Approved" } : r));
  };
  const handleReject = (id) => {
    setLeaveRequests((prev) => prev.map((r) => r.id === id ? { ...r, status: "Rejected" } : r));
  };

  // ── Filter ──
  const filtered = leaveRequests.filter((r) => {
    const matchSearch =
      (r.employeeName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.department   || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = statusFilter === "All status" || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div style={{ padding: "16px", background: "#f8f9fa", minHeight: "100vh", fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a2e" }}>Leave Management</h2>
        <p style={{ margin: "4px 0 0", color: "#6b7280", fontSize: 14 }}>Review and manage employee leave requests</p>
      </div>

      {/* ── Stat Cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 16, marginBottom: 24 }}>
        {[
          { label: "Pending Requests", value: pendingCount,       border: "#3b82f6" },
          { label: "Approved Today",   value: approvedTodayCount, border: "#22c55e" },
          { label: "Rejected",         value: rejectedCount,      border: "#ef4444" },
          { label: "On Leave Today",   value: onLeaveTodayCount,  border: "#f59e0b" },
        ].map((c) => (
          <div key={c.label} style={{
            background: "#fff", borderRadius: 10, padding: "20px 24px",
            borderTop: `4px solid ${c.border}`, boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
          }}>
            <div style={{ fontSize: 32, fontWeight: 700, color: "#1a1a2e" }}>{c.value}</div>
            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <input
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: 200, border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", fontSize: 14, outline: "none" }}
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "8px 14px", fontSize: 14, background: "#fff", cursor: "pointer" }}
        >
          {["All status", "Approved", "Pending", "Rejected"].map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </div>

      {/* ── Table ── */}
      <div style={{ background: "#fff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #f3f4f6" }}>
              {["Employee", "Leave Type", "From", "To", "Days", "Status", "Actions"].map((h) => (
                <th key={h} style={{ padding: "14px 16px", textAlign: "left", fontSize: 13, fontWeight: 600, color: "#6b7280" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>No records found</td>
              </tr>
            ) : (
              filtered.map((r, i) => (
                <tr key={r.id} style={{ borderBottom: "1px solid #f9fafb" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#fafafa"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{
                        width: 36, height: 36, borderRadius: "50%",
                        background: AVATAR_COLORS[i % AVATAR_COLORS.length],
                        color: "#fff", display: "flex", alignItems: "center",
                        justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0,
                      }}>
                        {getInitials(r.employeeName)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>{r.employeeName}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af" }}>{r.department}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "14px 16px", fontSize: 14, color: "#374151" }}>{r.leaveType}</td>
                  <td style={{ padding: "14px 16px", fontSize: 14, color: "#374151" }}>{r.fromDate}</td>
                  <td style={{ padding: "14px 16px", fontSize: 14, color: "#374151" }}>{r.toDate}</td>
                  <td style={{ padding: "14px 16px", fontSize: 14, color: "#374151" }}>{r.numberOfDays}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <span style={statusStyle(r.status)}>{r.status}</span>
                  </td>
                  <td style={{ padding: "14px 16px" }}>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => handleApprove(r.id)}
                        title="Approve"
                        style={{
                          width: 30, height: 30, borderRadius: 6,
                          border: "1px solid #e5e7eb",
                          background: r.status === "Pending" ? "#22c55e" : "#f9fafb",
                          color: r.status === "Pending" ? "#fff" : "#6b7280",
                          cursor: "pointer", fontWeight: 700,
                        }}
                      >✓</button>
                      <button
                        onClick={() => handleReject(r.id)}
                        title="Reject"
                        style={{
                          width: 30, height: 30, borderRadius: 6,
                          border: "1px solid #e5e7eb", background: "#f9fafb",
                          color: "#6b7280", cursor: "pointer", fontWeight: 700,
                        }}
                      >✕</button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Leave;